import { useState, useRef, useEffect } from "preact/hooks";
import { useMessage } from "../providers/MessageProvider";
import { useMute } from "../providers/MuteProvider";
import { useUserInteraction } from "../providers/UserInteractionProvider";
import { useAppMessage, useParticipantIds } from "@daily-co/daily-react";
import { useWidget } from "@/providers/WidgetProvider";
import { ArrowUp } from "lucide-react";
import { MicrophoneIcon, SpeakerWaveIcon, SpeakerXMarkIcon } from "@heroicons/react/16/solid";
import MicrophoneSlashIcon from "./icons/MicrophoneSlashIcon";
import { motion } from "motion/react";

const SENT_MSG_TIMEOUT = 5000; // 5 seconds
const SHOW_MICROPHONE = true;

export default function ActionBar() {
  const [msgKey, setMsgKey] = useState(0);
  const { lastMessage, setLastMessage, setQueuedMessage } = useMessage();
  const { isMicMuted, setIsMicMuted, isAudioMuted, setIsAudioMuted } = useMute();
  const { setHasUserInteracted } = useUserInteraction();
  const { hasJoinedRoom, isAgentVideoReady } = useWidget();
  const sendAppMessage = useAppMessage();
  const remoteParticipantIds = useParticipantIds({ filter: "remote" });
  const [message, setMessage] = useState<string>("");
  const [inputHeight, setInputHeight] = useState<number>(40);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messageTimeoutRef = useRef<number | null>(null);
  const [isAnimatingComposer] = useState(false);
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Fade out message after SENT_MSG_TIMEOUT seconds
  useEffect(() => {
    if (!lastMessage) {
      setShowMessage(false);
      return;
    }

    // new "instance" of the bubble (even if text is the same)
    const hasAgentJoinedCall = remoteParticipantIds.length > 0 && isAgentVideoReady;
    if (hasAgentJoinedCall) {
      setShowMessage(true);
      setIsExiting(false);
    }

    if (messageTimeoutRef.current !== null) {
      clearTimeout(messageTimeoutRef.current);
    }

    messageTimeoutRef.current = window.setTimeout(() => {
      setIsExiting(true);
      messageTimeoutRef.current = null;
    }, SENT_MSG_TIMEOUT);

    return () => {
      if (messageTimeoutRef.current !== null) {
        clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = null;
      }
    };
  }, [msgKey, lastMessage, remoteParticipantIds, isAgentVideoReady]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current && message) {
      textareaRef.current.style.height = "auto";
      const newHeight = textareaRef.current.scrollHeight;
      const cappedHeight = Math.min(newHeight, 128);
      textareaRef.current.style.height = `${newHeight}px`;
      setInputHeight(cappedHeight);
    } else if (textareaRef.current && !message) {
      textareaRef.current.style.height = "40px";
      setInputHeight(40);
    }
  }, [message]);

  const handleSend = () => {
    setHasUserInteracted(true);
    if (message.trim()) {
      const trimmedMessage = message.trim();
      setMessage("");
      if (hasJoinedRoom && remoteParticipantIds.length > 0) {
        setLastMessage(trimmedMessage);
        setMsgKey(k => k + 1);
        sendAppMessage(
          {
            event: "chat-msg",
            message: trimmedMessage,
            name: "Guest",
          },
          "*"
        );
      } else {
        setQueuedMessage(trimmedMessage);
      }
    } else {
      setMessage("");
    }
  };

  const handleMsgChange = (e: Event) => {
    const target = e.target as HTMLTextAreaElement;
    setMessage(target.value);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleMicMuteToggle = async () => {
    setHasUserInteracted(true);
    setIsMicMuted(!isMicMuted);
  };

  const handleAudioMuteToggle = () => {
    setIsAudioMuted(!isAudioMuted);
  };

  return (
    <>
      {/* Sent Message Display */}
      {showMessage && (
        <div className="absolute left-2 right-2 flex justify-center z-10" style={{ bottom: `${inputHeight + 24}px` }}>
          <div
            key={msgKey}
            onAnimationEnd={() => {
              if (isExiting) {
                setShowMessage(false);
                setIsExiting(false);
                setLastMessage("");
              }
            }}
            style={{
              animation: isExiting ? "fadeOutUp 0.2s ease-out forwards" : "fadeInUp 0.2s ease-out forwards",
            }}
            className={`mx-auto flex bg-black/20 backdrop-blur-xl text-white rounded-2xl px-3 py-2 
    text-sm break-all text-center 
    line-clamp-2`}
          >
            {lastMessage}
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="absolute bottom-0 left-0 right-0 p-2 pt-8 flex items-end gap-1 ">
        {/* <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: "linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.8))"
          }}
        /> */}
        <div class="absolute w-full h-full left-0 right-0 pointer-events-none bottom-0 z-0">
          <div class="absolute inset-0 backdrop-blur-[1px] mask-[linear-gradient(rgba(0,0,0,0),rgba(0,0,0,1)_10%,rgba(0,0,0,1)_30%,rgba(0,0,0,0)_40%)]"></div>
          <div class="absolute inset-0 backdrop-blur-[2px] mask-[linear-gradient(rgba(0,0,0,0)_10%,rgba(0,0,0,1)_20%,rgba(0,0,0,1)_40%,rgba(0,0,0,0)_50%)]"></div>
          <div class="absolute inset-0 backdrop-blur-[4px] mask-[linear-gradient(rgba(0,0,0,0)_15%,rgba(0,0,0,1)_30%,rgba(0,0,0,1)_50%,rgba(0,0,0,0)_60%)]"></div>
          <div class="absolute inset-0 backdrop-blur-[8px] mask-[linear-gradient(rgba(0,0,0,0)_20%,rgba(0,0,0,1)_40%,rgba(0,0,0,1)_60%,rgba(0,0,0,0)_70%)]"></div>
          <div class="absolute inset-0 backdrop-blur-[16px] mask-[linear-gradient(rgba(0,0,0,0)_40%,rgba(0,0,0,1)_60%,rgba(0,0,0,1)_80%,rgba(0,0,0,0)_90%)]"></div>
          <div class="absolute inset-0 backdrop-blur-[32px] mask-[linear-gradient(rgba(0,0,0,0)_60%,rgba(0,0,0,1)_80%)]"></div>
          <div class="absolute inset-0 z-10 backdrop-blur-[64px] mask-[linear-gradient(rgba(0,0,0,0)_70%,rgba(0,0,0,1)_100%)]"></div>
          <div class="absolute inset-0 bg-[linear-gradient(transparent,#000000)]"></div>
        </div>
        <motion.div className="flex items-center w-full z-10">
          {/* Composer */}
          <motion.div layout className="flex-1">
            <div
              className={`
                relative flex gap-0 items-center w-full bg-white/10 hover:bg-white/15 transition-colors duration-150 
                rounded-[22px] shadow-lg shadow-inset-white-10 min-h-11
                ${isComposerFocused ? "shadow-focused" : "shadow-inset-white-10"}
              `}
            >
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleMsgChange}
                onKeyDown={handleKeyDown}
                onInput={handleMsgChange}
                placeholder="Message"
                rows={1}
                onFocus={() => {
                  setIsComposerFocused(true);
                }}
                onBlur={() => {
                  setIsComposerFocused(false);
                }}
                className={`
                relative w-full flex-1 resize-none border-none outline-none pl-4  py-2 text leading-[1.4] max-h-36 overflow-y-auto text-white placeholder:text-white/70
                [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
                transition-opacity duration-150
                ${isAnimatingComposer ? "placeholder:opacity-0" : "placeholder:opacity-70"}
                ${!message.trim() ? "" : "pr-14"}
                `}
              />
              <div
                className={`
                  absolute right-0 bottom-0 top-0 transition-all duration-150 flex items-end p-2
                  ${
                    message.trim()
                      ? "opacity-100 blur-0 scale-100 pointer-events-auto"
                      : "opacity-0 blur-sm scale-95 pointer-events-none"
                  }
                `}
              >
                <button
                  onClick={handleSend}
                  className="py-1 px-3 rounded-3xl bg-white hover:bg-white/80 cursor-pointer transition-colors h-[28px]]"
                  aria-label="Send message"
                >
                  <ArrowUp strokeWidth={2} size={20} color="black" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Buttons — DO NOT UNMOUNT */}
          <motion.div
            layout
            animate={{
              opacity: message.trim() ? 0 : 1,
              scale: message.trim() ? 0.2 : 1,
              width: message.trim() ? 0 : "auto",
              filter: message.trim() ? "blur(20px)" : "blur(0px)",
            }}
            transition={{
              layout: { duration: 0.25, ease: "easeOut" },
              opacity: { duration: 0.25 },
              width: { duration: 0.25, ease: "easeOut" },
              scale: { duration: 0.25, ease: "easeOut" },
              filter: { duration: 0.25 },
            }}
            className={`flex items-center gap-1 flex-none pl-1 
              `}
          >
            {/* {${!message.trim()
              ? "opacity-100 scale-none"
              : "opacity-0 scale-25"
              }} */}
            {SHOW_MICROPHONE && (
              <button
                onClick={handleMicMuteToggle}
                className="w-11 h-11 flex-none rounded-full flex items-center justify-center bg-white/10 hover:bg-white/15 backdrop-blur-xl cursor-pointer transition-colors duration-150 shadow-inset-white-10"
              >
                {isMicMuted ? (
                  <MicrophoneSlashIcon className="size-5 text-white" />
                ) : (
                  <MicrophoneIcon className="size-5 text-white" />
                )}
              </button>
            )}

            <button
              onClick={handleAudioMuteToggle}
              className="w-11 h-11 flex-none rounded-full flex items-center justify-center bg-white/10 hover:bg-white/15 backdrop-blur-xl cursor-pointer transition-colors duration-150 shadow-inset-white-10"
            >
              {isAudioMuted ? (
                <SpeakerXMarkIcon className="size-5 text-white" />
              ) : (
                <SpeakerWaveIcon className="size-5 text-white" />
              )}
            </button>
            {/* </div> */}
          </motion.div>
        </motion.div>
      </div>
    </>
  );
}
